import type { WidgetComponent } from '@wix/chat-widget-common-widgets';
import { Text } from '@wix/design-system';
import React, { useEffect } from 'react';
import s from './FallbackWidget.module.scss';

interface FallbackWidgetHttpError {
  errorType: 'http';
  errorCode: number;
}
interface FallbackWidgetKeeperError {
  errorType: 'keeper';
  errorCode: 'legal' | 'relevancy';
}

const defaultReply = `Something unexpected happened. I'm not sure what it is, but I'm on it. Please try again or reach out for assistance.`;

export type FallbackWidgetData =
  | FallbackWidgetHttpError
  | FallbackWidgetKeeperError;

function rephraseHttpError({ errorCode }: { errorCode: number }): string {
  switch (errorCode) {
    case 400:
      return `Oops! Looks like there was a misunderstanding. Please double check your information and try again.`;
    case 401:
      return `It seems like you're not logged in. Please log in and try again.`;
    case 403:
      return `You don't have permission to do this. If you think this is a mistake, we can try to figure it out together.`;
    case 404:
      return `I couldn't find what you were looking for. Would you like to try a different approach? I'm here to guide you.`;
    case 408:
      return `The request is taking too long. Please check your connection and try again.`;
    case 429:
      return `Looks like I'm getting too many requests from you. Please slow down a little and try again later.`;
    case 500:
      return `Ah, something went wrong on my end. Sorry about that! I'm working on fixing it. Please try again in a bit.`;
    case 502:
      return `I'm having some trouble communicating with my servers. Please try again.`;
    case 503:
      return `My service is currently unavailable. I'm probably working on something exciting! Please try again later.`;
    case 504:
      return `It's taking longer than expected to get a response. Please try again.`;
    default:
      return defaultReply;
  }
}
function rephraseKeeperError({ errorCode }: { errorCode: string }): string {
  switch (errorCode) {
    case 'legal':
      return `Sorry, but I cannot answer that request. Is there something else I can help you with?`;
    case 'relevancy':
      return `Sorry, but I cannot help you with that. Is there something else I can help you with?`;
    default:
      return defaultReply;
  }
}

export const FallbackWidget: WidgetComponent<FallbackWidgetData> = ({
  context: { onWidgetAction },
  data,
}) => {
  const { errorType, errorCode } = data;
  useEffect(() => {
    if (onWidgetAction) {
      onWidgetAction(FALLBACK_WIDGET, { action: 'appeared' });
      onWidgetAction(FALLBACK_WIDGET, {
        action: 'error',
        value: {
          code: errorCode,
          type: errorType,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rephrasedError =
    errorType === 'http'
      ? rephraseHttpError({ errorCode })
      : rephraseKeeperError({ errorCode });

  return <Text className={s.text}>{rephrasedError}</Text>;
};

export const FALLBACK_WIDGET = 'FALLBACK_WIDGET';
